// Shared i18n wiring for `<EntityScreen>` filter-rail props.
//
// EntityScreen accepts host-supplied translator functions for chrome labels,
// field labels, option labels, and the facet panel chrome (Iron Rule #11 —
// the platform stays i18n-agnostic; the host wires it). Every route that
// renders EntityScreen funnels through this hook so the wiring stays DRY
// and consistent.
//
// The i18n catalog keys are namespaced under `filterRail.*` in
// `src/locales/{lang}.json`. When a `filterRail.value.<field>.<value>` key
// is missing, `formatOptionLabel` returns `undefined` so EntityScreen can
// fall back to its built-in formatter (this is intentional — codes like
// `CNST` have no useful translation when displayed as a chip).

import { useTranslation } from 'react-i18next';

export interface FilterRailLabels {
  intentLabels: {
    intent: string;
    placeholder: string;
    pressSlash: string;
    rows: string;
    of: string;
    aiBadge: (count: number) => string;
    aiTitle: string;
    ignored: string;
    nlPlaceholder: string;
  };
  formatFieldLabel: (fieldId: string, fallback: string) => string;
  formatOptionLabel: (fieldId: string, value: string) => string | undefined;
  facetChrome: { clr: string; more: string };
}

export function useFilterRailLabels(): FilterRailLabels {
  const { t } = useTranslation();

  return {
    intentLabels: {
      intent: t('filterRail.chrome.intent'),
      placeholder: t('filterRail.chrome.placeholder'),
      pressSlash: t('filterRail.chrome.pressSlash'),
      rows: t('filterRail.chrome.rows'),
      of: t('filterRail.chrome.of'),
      aiBadge: (count: number) => t('filterRail.chrome.aiBadge', { count }),
      aiTitle: t('filterRail.chrome.aiTitle'),
      ignored: t('filterRail.chrome.ignored'),
      nlPlaceholder: t('filterRail.chrome.nlPlaceholder'),
    },
    formatFieldLabel: (fieldId, fallback) =>
      t(`filterRail.label.${fieldId}`, { defaultValue: fallback }),
    formatOptionLabel: (fieldId, value) => {
      const key = `filterRail.value.${fieldId}.${value}`;
      const translated = t(key, { defaultValue: '' });
      return translated === '' ? undefined : translated;
    },
    facetChrome: {
      clr: t('filterRail.chrome.clr'),
      // Pass-through template — MultiSelectFacet substitutes {{count}} itself,
      // so we disable i18next interpolation here by swapping the delimiters.
      more: t('filterRail.chrome.more', {
        interpolation: { prefix: ' ', suffix: ' ' },
      }),
    },
  };
}
